import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircularProgress,
  Alert,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Container,
  Grid,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import HttpService from "../services/HttpService";
import UserService from "../services/UserService";
import { EmployerProvider } from "../contexts/EmployerContext";
import Labels from "../labels/Labels";

// Component for organization selection when multiple organizations are available
function EmployerSelectionComponent({ employers, onEmployerSelected }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // Show 6 organizations per page
  const showSearchAndPagination = employers.length > itemsPerPage; // Only show search/pagination if more than 6 orgs

  // Sort and filter organizations
  const filteredAndSortedEmployers = useMemo(() => {
    let filtered = employers;

    // Only filter by search term if search is enabled
    if (showSearchAndPagination && searchTerm) {
      filtered = employers.filter(
        (employer) =>
          employer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (employer.description &&
            employer.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }

    // Sort alphabetically by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    return filtered;
  }, [employers, searchTerm, showSearchAndPagination]);

  // Calculate pagination (only if search/pagination is enabled)
  const totalPages = showSearchAndPagination
    ? Math.ceil(filteredAndSortedEmployers.length / itemsPerPage)
    : 1;
  const startIndex = showSearchAndPagination
    ? (currentPage - 1) * itemsPerPage
    : 0;
  const displayedEmployers = showSearchAndPagination
    ? filteredAndSortedEmployers.slice(startIndex, startIndex + itemsPerPage)
    : filteredAndSortedEmployers;

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h4" gutterBottom color="primary" sx={{ mb: 1 }}>
          {Labels.getLabel(
            "employerAccessGuard.selectOrganization",
            "Select Your Organization"
          )}
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
        >
          {Labels.getLabel(
            "employerAccessGuard.accessMessage",
            "You have access to"
          )}{" "}
          {employers.length}{" "}
          {Labels.getLabel(
            "employerAccessGuard.organizations",
            "organizations. Please select one to continue:"
          )}
        </Typography>

        {/* Search Field - Only show if more than 6 organizations */}
        {showSearchAndPagination && (
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={Labels.getLabel(
                "employerAccessGuard.searchPlaceholder",
                "Search organizations..."
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3, maxWidth: 500 }}
            />

            {/* Results count */}
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={<BusinessIcon />}
                label={Labels.getLabel(
                  "employerAccessGuard.resultsCount",
                  "{0} organizations found"
                ).replace("{0}", filteredAndSortedEmployers.length)}
                variant="outlined"
                color="primary"
              />
            </Box>
          </>
        )}

        {/* No results message */}
        {showSearchAndPagination && filteredAndSortedEmployers.length === 0 && (
          <Alert severity="info" sx={{ mb: 3, maxWidth: 500 }}>
            {Labels.getLabel(
              "employerAccessGuard.noResults",
              "No organizations match your search. Try a different search term."
            )}
          </Alert>
        )}

        {/* Organization Grid */}
        {filteredAndSortedEmployers.length > 0 && (
          <>
            <Grid container spacing={2} sx={{ mb: 3, maxWidth: 800 }}>
              {displayedEmployers.map((employer) => {
                // Try different possible ID field names for compatibility
                const employerId =
                  employer.id || employer.idEmployer || employer.id_employer;

                return (
                  <Grid item xs={12} sm={6} key={employerId}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        height: "100%",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            onEmployerSelected(employerId);
                          }}
                          sx={{
                            p: 3,
                            justifyContent: "flex-start",
                            height: "100%",
                            border: "none",
                            borderRadius: 1,
                            "&:hover": {
                              border: "none",
                              backgroundColor: "primary.light",
                              color: "primary.contrastText",
                            },
                          }}
                        >
                          <Box textAlign="left" width="100%">
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={1}
                            >
                              <BusinessIcon color="primary" />
                              <Typography variant="h6" component="div" noWrap>
                                {employer.name}
                              </Typography>
                            </Box>
                            {employer.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {employer.description}
                              </Typography>
                            )}
                          </Box>
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination - Only show if more than 6 organizations and multiple pages */}
            {showSearchAndPagination && totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                color="primary"
                size="large"
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default function EmployerAccessGuard({ children }) {
  const [chosenEmployerId, setChosenEmployerId] = useState(() =>
    sessionStorage.getItem("chosenEmployerId")
  );
  const [isSwitching, setIsSwitching] = useState(false);
  const queryClient = useQueryClient();

  // When switching, clear session and chosenEmployerId
  const handleSwitch = () => {
    setIsSwitching(true);
    sessionStorage.removeItem("chosenEmployerId");
    setChosenEmployerId(null);
  };

  // Use effect to refetch when chosenEmployerId changes
  React.useEffect(() => {
    queryClient.invalidateQueries(["employerInfo"]);
  }, [chosenEmployerId]);

  async function fetchEmployerInfo() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer`
    );
    return response;
  }

  const {
    data: dataEmployer,
    isLoading: isFetchingEmployer,
    isError: isErrorEmployer,
  } = useQuery(["employerInfo"], fetchEmployerInfo, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Show loading spinner while fetching employer info
  if (isFetchingEmployer) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // Check if employer data is null or if there was an error
  if (isErrorEmployer || !dataEmployer?.data || dataEmployer.data === null) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        p={4}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            {Labels.getLabel(
              "employerAccessGuard.accessDenied",
              "Access Denied"
            )}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {Labels.getLabel(
              "employerAccessGuard.unauthorized",
              "You are not authorized to access this application. Your user account is not associated with a valid organization."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Labels.getLabel(
              "employerAccessGuard.contactAdmin",
              "Please contact your administrator for assistance."
            )}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => UserService.doLogout()}
          sx={{ mt: 2 }}
        >
          {Labels.getLabel("employerAccessGuard.signOut", "Sign Out")}
        </Button>
      </Box>
    );
  }

  const employerData = dataEmployer?.data;
  const currentHasMultiple =
    Array.isArray(employerData) && employerData.length > 1;
  const storedHasMultiple =
    sessionStorage.getItem("hasMultipleEmployers") === "true";

  // Store the fact that user has multiple employers for future reference
  if (currentHasMultiple && !storedHasMultiple) {
    sessionStorage.setItem("hasMultipleEmployers", "true");
  }

  // Determine if user actually has multiple employers (either currently detected or previously stored)
  const hasMultipleEmployers = currentHasMultiple || storedHasMultiple;

  // Auto-set chosen organization if only one organization is returned and none is chosen
  // But don't auto-select if user is actively switching organizations
  if (
    Array.isArray(employerData) &&
    employerData.length === 1 &&
    !chosenEmployerId &&
    !isSwitching
  ) {
    const singleEmployerId =
      employerData[0].id ||
      employerData[0].idEmployer ||
      employerData[0].id_employer;
    if (singleEmployerId) {
      sessionStorage.setItem("chosenEmployerId", singleEmployerId);
      setChosenEmployerId(singleEmployerId);
    }
  }
  // Handle case where API returns single organization object (not array)
  if (
    !Array.isArray(employerData) &&
    employerData &&
    !chosenEmployerId &&
    !isSwitching
  ) {
    const singleEmployerId =
      employerData.id || employerData.idEmployer || employerData.id_employer;
    if (singleEmployerId) {
      sessionStorage.setItem("chosenEmployerId", singleEmployerId);
      setChosenEmployerId(singleEmployerId);
    }
  }

  // Show selection UI if multiple orgs and none chosen
  if (currentHasMultiple && !chosenEmployerId) {
    // Reset switching flag when showing selection UI
    if (isSwitching) {
      setIsSwitching(false);
    }
    return (
      <EmployerSelectionComponent
        employers={employerData}
        onEmployerSelected={(employerId) => {
          sessionStorage.setItem("chosenEmployerId", employerId);
          setChosenEmployerId(employerId);
          sessionStorage.setItem("hasMultipleEmployers", "true");
          queryClient.invalidateQueries(["employerInfo"]);
        }}
      />
    );
  }

  // Single organization or organization already chosen - continue normally
  const employerValue = {
    employerData: Array.isArray(employerData) ? employerData[0] : employerData,
    chosenEmployerId,
    hasMultipleEmployers,
    isLoadingEmployer: isFetchingEmployer,
    onSwitchEmployer: hasMultipleEmployers ? handleSwitch : null,
  };

  return <EmployerProvider value={employerValue}>{children}</EmployerProvider>;
}
